import { BUILDINGS, type Building, BuildingType, dollars, footprint } from './buildings';
import { TICKS_PER_GAME_HOUR, TICKS_PER_GAME_MINUTE } from './constants';

/*
 * Winter on the runways. Snow settles on each runway while it snows, the faster the heavier it
 * falls; the ground crews' routine ploughing keeps up with light and moderate snow, not with
 * heavy snow, and nobody's with the blizzard every winter brings. A runway buried past
 * SNOW_CLOSE closes, and stays closed until the player sends the plough team out
 * (dispatchPlows): it drives over from the ground handling base (without one, a contractor
 * comes in from the road, for a fee), ploughs the runway in two passes, out along one half and
 * back along the other, throwing the snow off to the sides, and drives home. The runway opens as
 * the second pass ends. One nobody sends the ploughs to is cleared by the crews themselves in
 * the end, after SNOW_WAIT.
 */

/** How hard it snows. Saved: append only. */
export const Snowfall = { None: 0, Light: 1, Moderate: 2, Heavy: 3, Blizzard: 4 } as const;
export type SnowfallId = (typeof Snowfall)[keyof typeof Snowfall];

/** Snow settling on a runway per hour (0–100 scale), by snowfall. */
export const SNOW_PER_HOUR: readonly number[] = [0, 4, 9, 18, 32];
/** Snow the crews' routine ploughing takes off an open runway per hour, by ground handling base
 * level (0: a contractor, paid by the hour). */
export const SNOW_CLEARING: readonly number[] = [6, 8, 11, 14, 18];
export const SNOW_CONTRACTOR_FEE = dollars(600);
/** Deep enough to worry about (the crews are falling behind), to close the runway, and to send
 * the ploughs out for. */
export const SNOW_WARN = 40;
export const SNOW_CLOSE = 60;
export const SNOW_DISPATCH = 20;
/** A closed runway nobody sends the ploughs to: the crews go out by themselves after this. */
export const SNOW_WAIT = 12 * TICKS_PER_GAME_HOUR;

/** Ploughs in a team, by ground handling base level (0: the contractor's). */
export const PLOUGH_TEAM: readonly number[] = [2, 2, 3, 4, 5];
/** Out of the garage, then tiles per game minute to the runway and back. */
export const PLOUGH_READY_MINUTES = 2;
export const PLOUGH_SPEED = 9;
/** Each of the two passes along the runway. */
export const PLOUGH_PASS_MINUTES = 8;
/** The contractor, from the road. */
export const PLOUGH_CONTRACTOR_MINUTES = 18;
export const PLOUGH_CONTRACTOR_FEE = dollars(8_000);

/** Every winter one blizzard: its first hour on one of these days of the season, and how long. */
export const BLIZZARD_FIRST_DAY = 2;
export const BLIZZARD_DAYS = 4;
export const BLIZZARD_MIN_HOURS = 4;
export const BLIZZARD_HOURS_SPAN = 4;
/** The met office warns this long before. */
export const BLIZZARD_WARNING = 6 * TICKS_PER_GAME_HOUR;

/** Snow lying on a runway (0–100). Runways with none have no entry. */
export interface RunwaySnow {
  readonly runwayId: number;
  depth: number;
}

/** A plough team's run to a runway and back (ticks, each on a whole game minute). */
export interface SnowJob {
  readonly runwayId: number;
  /** The ground handling base it comes from (0: the contractor, from the road). */
  readonly baseId: number;
  /** Ploughs in the team. */
  readonly team: number;
  /** Sent out. */
  readonly from: number;
  /** On the runway: the first pass begins. */
  readonly arrive: number;
  /** At the far end: the second pass begins, back along the other half. */
  readonly turn: number;
  /** The runway is clear (and open again). */
  readonly done: number;
  /** Back at the base (or gone). */
  readonly home: number;
  /** The runway end they begin at: 0 its start (west or north), 1 the far end. */
  readonly end: number;
}

const minutes = (m: number) => m * TICKS_PER_GAME_MINUTE;

/** The two ends of a runway's centre line (tiles), its start first. */
export function runwayEnds(rw: Building): readonly [[number, number], [number, number]] {
  const { w, h } = footprint(BUILDINGS[rw.type], rw.rot);
  return w >= h
    ? [
        [rw.x, rw.y + h / 2],
        [rw.x + w, rw.y + h / 2],
      ]
    : [
        [rw.x + w / 2, rw.y],
        [rw.x + w / 2, rw.y + h],
      ];
}

/**
 * The team that would go to a runway sent out at `time`: from the nearest ground handling base,
 * or the contractor from the road (`roadY`: the row the road comes in on). Starts on the next
 * whole minute.
 */
export function ploughJob(
  buildings: readonly Building[],
  rw: Building,
  time: number,
  roadY: number,
): SnowJob {
  const start = Math.ceil(time / TICKS_PER_GAME_MINUTE) * TICKS_PER_GAME_MINUTE;
  const [a, b] = runwayEnds(rw);
  let base: Building | null = null;
  let best = 0;
  let end = 0;
  for (const g of buildings) {
    if (g.type !== BuildingType.GroundHandling || g.level < 1) continue;
    const { w, h } = footprint(BUILDINGS[g.type], g.rot);
    const cx = g.x + w / 2;
    const cy = g.y + h / 2;
    const da = Math.abs(cx - a[0]) + Math.abs(cy - a[1]);
    const db = Math.abs(cx - b[0]) + Math.abs(cy - b[1]);
    const d = Math.min(da, db);
    if (base && d >= best) continue;
    base = g;
    best = d;
    end = db < da ? 1 : 0;
  }
  const travel = base
    ? minutes(PLOUGH_READY_MINUTES + Math.ceil(best / PLOUGH_SPEED))
    : minutes(PLOUGH_CONTRACTOR_MINUTES);
  if (!base) end = Math.abs(b[1] - roadY) < Math.abs(a[1] - roadY) ? 1 : 0;
  const arrive = start + travel;
  const turn = arrive + minutes(PLOUGH_PASS_MINUTES);
  const done = turn + minutes(PLOUGH_PASS_MINUTES);
  return {
    runwayId: rw.id,
    baseId: base?.id ?? 0,
    team: PLOUGH_TEAM[base ? Math.min(4, base.level) : 0] ?? 2,
    from: start,
    arrive,
    turn,
    done,
    home: done + travel,
    end,
  };
}

/** Snow lying on a runway (0–100). */
export function snowDepth(snow: readonly RunwaySnow[], runwayId: number): number {
  return snow.find((s) => s.runwayId === runwayId)?.depth ?? 0;
}

/** Sets the snow on a runway (an entry only while there is some). */
export function setSnowDepth(snow: RunwaySnow[], runwayId: number, depth: number): void {
  const i = snow.findIndex((s) => s.runwayId === runwayId);
  if (depth <= 0) {
    if (i >= 0) snow.splice(i, 1);
  } else if (i >= 0) {
    (snow[i] as RunwaySnow).depth = depth;
  } else {
    snow.push({ runwayId, depth });
  }
}
